/**
 * Helpers for short-lived presigned S3 GET URLs returned by the API.
 */

import { getS3DownloadUrl } from '../clients/s3_media_client';
import { defaultImageUrl, defaultPdfUrl } from '../config_and_helpers/config';

const MATERIAH_BUCKET_HOST = 'materiah1.s3';
const PRESIGNED_EXPIRY_BUFFER_SEC = 60;

/** Public/static paths that do not require download-url refresh. */
const PUBLIC_PATH_PREFIXES = [
  '/design/',
  '/products/No-Image-Placeholder',
  '/products/PDF-Placeholder',
];

/**
 * @param {string} [url]
 * @returns {boolean}
 */
export function isMateriahS3Url(url) {
  if (!url || typeof url !== 'string') return false;
  return url.includes(MATERIAH_BUCKET_HOST) || url.includes('materiah1.s3.');
}

/**
 * Static branding/placeholder assets in config — not upload presigns.
 *
 * @param {string} [url]
 * @returns {boolean}
 */
export function isStaticPublicMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (url === defaultImageUrl || url === defaultPdfUrl) return true;
  if (!isMateriahS3Url(url)) return false;
  try {
    const pathname = new URL(url).pathname;
    return PUBLIC_PATH_PREFIXES.some(prefix => pathname.includes(prefix));
  } catch {
    return false;
  }
}

/**
 * Materiah upload media that may need presigned refresh (not static placeholders).
 *
 * @param {string} [url]
 * @returns {boolean}
 */
export function isMateriahPrivateMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!isMateriahS3Url(url)) return false;
  if (isStaticPublicMediaUrl(url)) return false;
  return true;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isPresignedUrlLikelyExpired(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    const amzDate = parsed.searchParams.get('X-Amz-Date');
    const expiresSec = Number(parsed.searchParams.get('X-Amz-Expires'));
    if (!amzDate || !Number.isFinite(expiresSec) || expiresSec <= 0) {
      return false;
    }
    const startMs = Date.parse(
      amzDate.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        '$1-$2-$3T$4:$5:$6Z'
      )
    );
    if (!Number.isFinite(startMs)) return false;
    const expiryMs = startMs + expiresSec * 1000;
    return Date.now() >= expiryMs - PRESIGNED_EXPIRY_BUFFER_SEC * 1000;
  } catch {
    return false;
  }
}

/**
 * Whether FE should call download-url before using the URL.
 *
 * @param {string} [url]
 * @returns {boolean}
 */
export function shouldRefreshMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!isMateriahPrivateMediaUrl(url)) return false;
  if (!url.includes('X-Amz-')) return true;
  return isPresignedUrlLikelyExpired(url);
}

/**
 * Map UI context to download-url query params.
 *
 * @param {Object} [context]
 * @param {number|string} [context.quoteId]
 * @param {number|string} [context.productImageId]
 * @param {number|string} [context.orderImageId] - OrderImage row pk (file_id)
 * @returns {Object} Params for {@link getS3DownloadUrl}
 */
export function buildDownloadUrlParamsFromContext(context = {}) {
  if (context.quoteId != null && context.quoteId !== '') {
    return { quoteId: context.quoteId };
  }
  if (context.productImageId != null && context.productImageId !== '') {
    return { productImageId: context.productImageId };
  }
  if (context.orderImageId != null && context.orderImageId !== '') {
    return { orderImageId: context.orderImageId };
  }
  return {};
}

/**
 * Try to extract s3 object key from a Materiah S3 URL for download-url fallback.
 *
 * @param {string} url
 * @returns {string}
 */
export function extractS3KeyFromUrl(url) {
  if (!url) return '';
  try {
    const pathname = new URL(url).pathname;
    return pathname.startsWith('/') ? pathname.slice(1) : pathname;
  } catch {
    return '';
  }
}

/**
 * Resolve a media URL for display or download (refresh presigned GET when needed).
 *
 * @async
 * @param {string|null|undefined} token
 * @param {string} url
 * @param {Object} [context] - quoteId, productImageId, orderImageId, s3Key
 * @returns {Promise<string>}
 */
export async function resolveMediaUrl(token, url, context = {}) {
  if (!url || typeof url !== 'string') return url || '';
  if (!shouldRefreshMediaUrl(url)) return url;
  if (!token) return url;

  const params = buildDownloadUrlParamsFromContext(context);
  if (Object.keys(params).length === 0) {
    return url;
  }

  const result = await getS3DownloadUrl(token, params);
  if (result.success && result.download_url) {
    return result.download_url;
  }
  return url;
}

/**
 * Open URL in a new tab after optional presigned refresh.
 *
 * @async
 * @param {string|null|undefined} token
 * @param {string} url
 * @param {Object} [context]
 * @returns {Promise<boolean>} false if blocked or no url
 */
export async function openMediaUrl(token, url, context = {}) {
  const resolved = await resolveMediaUrl(token, url, context);
  if (!resolved) return false;
  window.open(resolved, '_blank', 'noopener,noreferrer');
  return true;
}
